import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { mapTo, mergeMap, tap } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { WorkWeekdayModel } from '../../models/complex/work-weekday.model';
import { WorkdayModel } from '../../models/low-level/workday.model';
import { FormatDurationPipe } from '../../pipes/format-duration/format-duration.pipe';
import { ConfigService } from '../../services/config/config.service';
import { WeeklyWorkTimeFileStorageService } from './weekly-work-time-file-storage.service';

export class StatusBar {
    public constructor(
        public type?: string,
        public text?: string,
    ) {}
}

@Injectable({
    providedIn: 'root',
})
export class WeeklyWorkTimeService {
    private workTimeModel: WorkWeekdayModel;
    private updateWeeklyWorkTimeValueSubject: BehaviorSubject<WorkdayModel[]>;
    public updateWeeklyWorkTimeValue$: Observable<WorkdayModel[]>;
    public displayWeeklyWorkTime: boolean;
    public constructor(
        private readonly translate: TranslateService,
        private readonly weeklyWorkTimeFileStorageService: WeeklyWorkTimeFileStorageService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly formatDuration: FormatDurationPipe,
        private readonly configService: ConfigService,
    ) {
        this.updateWeeklyWorkTimeValueSubject = new BehaviorSubject<WorkdayModel[]>([]);
        this.updateWeeklyWorkTimeValue$ = this.updateWeeklyWorkTimeValueSubject.asObservable();
    }

    /**
     * This function is to get weekly work time list from file storage
     */
    public init(): Observable<void> {
        this.displayWeeklyWorkTime = this.configService.getDisplayWeeklyWorkTime();

        if (!this.displayWeeklyWorkTime) {
            return of(undefined);
        }
        return this.weeklyWorkTimeFileStorageService.getWeeklyWorkModel().pipe(
            mergeMap((workTimeModel) => {
                if (typeof this.workTimeModel === 'undefined' || this.workTimeModel.workdayModelList.length === 0) {
                    this.workTimeModel = workTimeModel;
                    return this.createWeeklyWorkTimeList();
                }
                return of(undefined);
            }),
            tap(() => this.updateWeeklyWorkTimeValueSubject.next(this.workTimeModel.workdayModelList)),
        );
    }

    /**
     * Following logic updates the Side Menu's Weekly Work Time Summary if
     * and only if ConfigSet.json DisplayWeeklyWorkTime is set to true ('X')
     * else, without the check it will attempt to update summary with an empty workTimeModel
     * and thus fail SHP-7454
     */
    public getWeeklyWorkTimeValueSubjectUpdate(): void {
        if (this.displayWeeklyWorkTime) {
            this.updateWeeklyWorkTimeValueSubject.next(this.workTimeModel.workdayModelList);
        }
    }

    /**
     * This function is to recreate weekly work time list when user go back to background mode
     */
    public autoUpdateWeeklyWorkTime(): Observable<void> {
        if (!this.displayWeeklyWorkTime) {
            return of(undefined);
        }
        const isTodayWorkTimeAvailable = this.workTimeModel.workdayModelList.some((workTime) =>
            workTime.workDate.isSame(moment(), 'days'),
        );

        if (!isTodayWorkTimeAvailable) {
            this.log.debug(
                `WeeklyWorkTimeService: autoUpdateWeeklyWorkTime: work time for date ${moment().toString()} is not available, re-creating work time data`,
            );
            return this.createWeeklyWorkTimeList().pipe(
                tap(() => this.updateWeeklyWorkTimeValueSubject.next(this.workTimeModel.workdayModelList)),
            );
        }
        return of(undefined);
    }

    /**
     * The weekly work time data has to be cleared after sunday 23:59,
     * So the new weekly work list will be created on every monday or every new week and save it to local storage
     *
     * The For loop is to add all the missing dates
     * Example:  If user re-installed pace app on Friday, then the data from Monday to Thursday will be missing in the list,
     * So, using for loop we are creating all the previous missing weeklyWorkTime with the particular date and with 0 hours duration
     */
    private createWeeklyWorkTimeList(): Observable<void> {
        // If it is new week, clear the old week's work time list data, if any
        if (this.workTimeModel.workdayModelList.length > 0 && this.isNewWeek()) {
            this.log.debug(`WeeklyWorkTimeService: Creating weekly work time for new week ${moment().week()}`);
            this.workTimeModel.workdayModelList = [];
            return this.saveWeeklyWorkTimeList();
        }

        // As per the ISO_8601: https://en.wikipedia.org/wiki/ISO_8601#Week_dates
        // Monday is considered as Beginning/Start of the week i.e  Monday as day 1
        // Sunday is considered as End of the week i.e Sunday as Day 7

        const currentWeekDay = moment().isoWeekday();

        for (let i = currentWeekDay - 1; i >= 0; i--) {
            const today = moment();
            today.subtract(i, 'day');

            const currentDateIndex = <number>this.getWorkDayObjectByDate(today, true);

            if (currentDateIndex < 0) {
                this.log.debug(`WeeklyWorkTimeService: Creating new work day for ${today}`);
                const workTimeDetails = new WorkdayModel(today);
                this.workTimeModel.workdayModelList.push(workTimeDetails);
            }
        }
        return of(undefined);
    }

    /**
     * This function gets called when timesheet gets submits
     * Possibilities , user can submit timesheet multiple times, so we need to check, if particular date has any previously saved timesheet duration
     * If yes, then we need to add the old timesheet duration to new duration
     *
     * @param date
     * @param duration
     */
    public updateWorkTimeListFromTimeSheet(date: moment.Moment, duration: moment.Duration): Observable<void> {
        const currentDateIndex = <number>this.getWorkDayObjectByDate(date, true);

        if (currentDateIndex === -1) {
            this.log.error(
                `WeeklyWorkTimeService: updateWorkTimeListFromTimeSheet : work time for date ${date.toString()} is not found`,
            );
            return of(undefined);
        }

        this.workTimeModel.workdayModelList[currentDateIndex].workDuration =
            this.workTimeModel.workdayModelList[currentDateIndex].previousTimesheetDuration.add(duration);
        this.log.debug(
            `WeeklyWorkTimeService: updateWorkTimeListFromTimeSheet : Duration : ${
                this.workTimeModel.workdayModelList[currentDateIndex].workDuration
            } for Date: ${date.toString()} `,
        );
        return this.saveWeeklyWorkTimeList();
    }

    /**
     * This function is to save the weekly work time to Local storage as well as to memory variable
     */
    public saveWeeklyWorkTimeList(): Observable<void> {
        const weeklyWorkModel = new WorkWeekdayModel(this.workTimeModel.workdayModelList);
        return this.weeklyWorkTimeFileStorageService.saveWeeklyWorkModel(weeklyWorkModel).pipe(
            tap(() => {
                this.setWorkTimeModel(weeklyWorkModel);
            }),
            mapTo(undefined),
        );
    }

    /**
     * This is public function to get the weekly work time model from memory variable
     */

    public getWeeklyWorkTimeList(): WorkdayModel[] {
        return this.workTimeModel.workdayModelList;
    }

    /**
     * This is public function to set the weekly work time model to memory variable
     *
     * @param workWeekdayModel
     */
    public setWorkTimeModel(workWeekdayModel: WorkWeekdayModel): void {
        this.workTimeModel = workWeekdayModel;
    }

    /**
     * This function is to calculate the sum of all the work time Duration of the week
     *
     * @param formatDuration to format the duration if formatDuration = true else return the duration in hours
     */
    public getTotalWeekDuration(formatDuration = true): string | number {
        const duration = this.workTimeModel.workdayModelList.reduce(
            (previousValue, currentValue) => previousValue + currentValue.workDuration.asHours(),
            0,
        );

        return formatDuration ? this.formatDuration.transform(duration) : duration;
    }

    /**
     * Generic method to return either work time model or index of work day model for matched date
     *
     * @param date
     * @param returnIndex
     */
    private getWorkDayObjectByDate(date: moment.Moment, returnIndex = false): number | WorkdayModel {
        const workDayIndex = this.workTimeModel.workdayModelList.findIndex((workDayModel) =>
            workDayModel.workDate.isSame(date, 'days'),
        );

        if (returnIndex) {
            return workDayIndex;
        }

        return this.workTimeModel.workdayModelList[workDayIndex];
    }

    /**
     * New Duration should be updated only for the given requested date,
     * If date did not match the we have to update previous Timesheet duration
     *
     * @param date
     */
    private updateWorkTimeList(date: moment.Moment): void {
        this.workTimeModel.workdayModelList.map((workDayModel) => {
            if (!workDayModel.workDate.isSame(date, 'days')) {
                workDayModel.workDuration = workDayModel.previousTimesheetDuration;
            }
        });
    }

    public updateTotalDurationForSpecificDate(date: moment.Moment, newDuration: moment.Duration): void {
        this.updateWorkTimeList(date);

        const workTime = <WorkdayModel>this.getWorkDayObjectByDate(date);

        if (!workTime) {
            this.log.error(
                `WeeklyWorkTimeService:updateWorkTimeList:  Work Time model for the date ${date} is not found`,
            );
            return;
        }

        workTime.workDuration = newDuration.add(workTime.previousTimesheetDuration);
    }

    /**
     * Since the list is already stored, so picking the first object date from the list as Start date
     * if the days difference from start date to current date is more than 7, then we are considering it as newWeek
     *
     * We are already storing currentWeek, so comparing stored week number with current day's week number
     * Example:
     * User submits Timesheet i.e on 11th sep 2021 (saturday), the week number is 36
     * User opens application on 14th sep 2021(monday), the week number is 37
     * So, 36!== 37 then it is a new week.
     */
    private isNewWeek(): boolean {
        const sevenDays = 7;

        const startDate = this.workTimeModel.workdayModelList[0].workDate;
        const numberOfDaysDifference = moment().diff(startDate, 'days');

        if (moment().day() === 0) {
            return numberOfDaysDifference >= sevenDays;
        }
        return this.workTimeModel.currentWeek !== moment().isoWeek();
    }

    public getStatusBarDetails(isWorkStartedForToday: boolean): StatusBar | undefined {
        const workStatusBar = new StatusBar();
        const currentDayWorkModel = <WorkdayModel>this.getWorkDayObjectByDate(moment());

        if (!currentDayWorkModel) {
            return;
        }
        const currentWorkDuration = currentDayWorkModel.workDuration.asHours();
        const formattedWorkDuration = this.formatDuration.transform(currentDayWorkModel.workDuration.asHours());

        if (
            currentWorkDuration >= ConfigConstants.nineHoursWorkDuration &&
            currentWorkDuration < ConfigConstants.totalWorkingHours
        ) {
            workStatusBar.type = ConfigConstants.STATUSBAR_TYPE.WARNING;
            workStatusBar.text = <string>this.translate.instant('WEEKLY_WORK_WORK_TODAY_IN_PROGRESS_LABEL', {
                workHours: formattedWorkDuration,
            });
            return workStatusBar;
        }

        if (currentWorkDuration >= ConfigConstants.totalWorkingHours) {
            workStatusBar.type = ConfigConstants.STATUSBAR_TYPE.CUSTOM;
            workStatusBar.text = <string>this.translate.instant('WEEKLY_WORK_WORK_HOUR_EXCEEDED_LABEL', {
                workHours: formattedWorkDuration,
            });
            return workStatusBar;
        }
        if (isWorkStartedForToday) {
            workStatusBar.type = ConfigConstants.STATUSBAR_TYPE.DEFAULT;
            workStatusBar.text = <string>this.translate.instant('WEEKLY_WORK_WORK_TODAY_IN_PROGRESS_LABEL', {
                workHours: formattedWorkDuration,
            });
            return workStatusBar;
        } else {
            workStatusBar.type = ConfigConstants.STATUSBAR_TYPE.SUCCESS;
            workStatusBar.text = <string>this.translate.instant('WEEKLY_WORK_WORK_TODAY_COMPLETED_LABEL', {
                workHours: formattedWorkDuration,
            });
            return workStatusBar;
        }
    }
}
